import React, { useState } from 'react';
import { familleTypeArticleService } from '../../../../services/familleTypeArticleService';
import { useTheme } from '../../../../contexts/ThemeContext';

const AddFamilleTypeArticleModal = ({ isOpen, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  
  const [formData, setFormData] = useState({
    designation: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.designation.trim()) {
        throw new Error('La désignation est obligatoire');
      }

      const result = await familleTypeArticleService.createFamilleTypeArticle(formData);
      
      if (result.success) {
        onSuccess();
        resetForm();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      designation: ''
    });
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className={`px-6 py-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-3">
              <i className="fas fa-plus text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Nouvelle Famille de Type d'Article</h2>
              <p className={`text-sm ${isDarkMode ? 'text-primary-200' : 'text-primary-100'}`}>Ajouter une nouvelle famille de type d'article</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Message */}
          {error && (
            <div className={`mb-6 p-4 rounded-lg border-l-4 flex items-center ${
              isDarkMode
                ? 'bg-red-900 border-red-800 text-red-200'
                : 'bg-red-50 border-red-500 text-red-700'
            }`}>
              <i className={`fas fa-exclamation-circle mr-2 ${isDarkMode ? 'text-red-300' : 'text-red-500'}`}></i>
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Information */}
            <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <i className="fas fa-info-circle mr-2" style={{ color: isDarkMode ? '#60a5fa' : '' }}></i>
                Informations de la famille
              </h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Désignation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                    placeholder="Ex: RIA, Extincteur, PIA, AUTO, UMM..."
                    required
                  />
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Entrez une désignation courte et descriptive pour cette famille
                  </p>
                </div>
              </div>
            </div>

            {/* Examples */}
            <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : ''}`}>
                <i className={`fas fa-lightbulb mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
                Exemples de familles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                  <div className="flex items-center mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                      <i className={`fas fa-fire-extinguisher text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}></i>
                    </div>
                    <span className={`${isDarkMode ? 'font-medium text-white' : 'font-medium text-gray-900'}`}>RIA</span>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Robinet d'Incendie Armé</p>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                  <div className="flex items-center mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${isDarkMode ? 'bg-red-900' : 'bg-red-100'}`}>
                      <i className={`fas fa-spray-can text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}></i>
                    </div>
                    <span className={`${isDarkMode ? 'font-medium text-white' : 'font-medium text-gray-900'}`}>Extincteur</span>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Extincteurs portables</p>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                  <div className="flex items-center mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${isDarkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                      <i className={`fas fa-shield-alt text-sm ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}></i>
                    </div>
                    <span className={`${isDarkMode ? 'font-medium text-white' : 'font-medium text-gray-900'}`}>PIA</span>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Poteau d'Incendie</p>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                  <div className="flex items-center mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${isDarkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                      <i className={`fas fa-cog text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}></i>
                    </div>
                    <span className={`${isDarkMode ? 'font-medium text-white' : 'font-medium text-gray-900'}`}>AUTO</span>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Système automatique</p>
                </div>
              </div>
            </div>

            {/* Preview */}
            {formData.designation && (
              <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-primary-50 border-primary-200'}`}>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : ''}`}>
                  <i className={`fas fa-eye mr-2 ${isDarkMode ? 'text-primary-300' : 'text-primary-600'}`}></i>
                  Aperçu
                </h3>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-primary-200'} rounded-lg p-4 border`}>
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${isDarkMode ? 'bg-primary-900' : 'bg-primary-100'}`}>
                      <i className={`fas fa-sitemap text-lg ${isDarkMode ? 'text-primary-300' : 'text-primary-600'}`}></i>
                    </div>
                    <div>
                      <h4 className={`${isDarkMode ? 'font-semibold text-white' : 'font-semibold text-gray-900'}`}>{formData.designation}</h4>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Famille de type d'article</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={`flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 rounded-lg transition-colors font-medium ${isDarkMode ? 'text-gray-300 bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Créer la famille
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);
};

export default AddFamilleTypeArticleModal;